import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Lightbulb as LightbulbIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  callId?: string;
  priority: string;
  actionUrl: string;
  read: boolean;
}

interface NotificationSummary {
  total: number;
  urgent: number;
  high: number;
  medium: number;
  byType: {
    critical: number;
    overdue: number;
    escalation: number;
    transfer: number;
    suggestion: number;
  };
}

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/notifications');
      
      if (!response.ok) throw new Error('Failed to fetch notifications');
      
      const data = await response.json();
      setNotifications(data.notifications || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (notifications.length === 0) {
      fetchNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    navigate(notification.actionUrl);
    handleClose();
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch('/api/notifications/read-all', { method: 'POST' });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'overdue': return <PhoneIcon color="warning" />;
      case 'escalation': return <TrendingUpIcon color="error" />;
      case 'transfer': return <PersonIcon color="warning" />;
      case 'suggestion': return <LightbulbIcon color="info" />;
      default: return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now.getTime() - notifTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return notifTime.toLocaleDateString();
  };

  const open = Boolean(anchorEl);
  const badgeCount = summary?.urgent || 0;

  return (
    <Box>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={badgeCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 400, maxHeight: 600 }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip 
                label={summary?.total || 0} 
                size="small" 
                color="primary"
              />
              {notifications.length > 0 && (
                <IconButton size="small" onClick={handleMarkAllRead}>
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Summary Chips */}
          {summary && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {summary.urgent > 0 && (
                <Chip 
                  label={`${summary.urgent} Urgent`} 
                  size="small" 
                  color="error"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {summary.byType.critical > 0 && (
                <Chip 
                  label={`${summary.byType.critical} Critical`} 
                  size="small" 
                  variant="outlined"
                  color="error"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {summary.byType.overdue > 0 && (
                <Chip 
                  label={`${summary.byType.overdue} Overdue`} 
                  size="small" 
                  variant="outlined"
                  color="warning"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Notifications List */}
        <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={40} />
            </Box>
          )}

          {error && (
            <Box p={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {!loading && !error && notifications.length === 0 && (
            <Box p={3} textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                All caught up! No new notifications.
              </Typography>
            </Box>
          )}

          {!loading && !error && notifications.length > 0 && (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 2,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      },
                      borderLeft: `4px solid`,
                      borderLeftColor: notification.priority === 'urgent' ? 'error.main' : 
                                      notification.priority === 'high' || notification.priority === 'critical' ? 'warning.main' : 
                                      'transparent'
                    }}
                  >
                    <ListItemIcon>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority) as any}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Button 
                fullWidth 
                size="small"
                onClick={() => {
                  navigate('/notifications');
                  handleClose();
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default NotificationCenter;

